import {
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageCard from 'src/components/PageCard';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import CachedOutlinedIcon from '@mui/icons-material/CachedOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LoadingIconButton from 'src/components/LoadingIconButton';
import LoadingButton from 'src/components/LoadingButton';
import RoleBased from 'src/components/RoleBased';
import {
  PortalRole,
  useListUsersQuery,
  useUpdateUserDeletedStatusMutation,
  useListCustomersQuery,
} from 'src/backend';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Search } from '@mui/icons-material';
import { formatDateTime } from 'src/utils';
import CreateUserDialog from './CreateUserDialog';
import EditUserDialog from './EditUserDialog';

export type User = {
  id: string;
  name: string;
  email: string;
  role: PortalRole;
  isDeleted: boolean;
  createdAt: string;
  createdBy: string;
  lastModifiedAt: string;
  lastModifiedBy: string;
  customerIds: string[];
};

function CustomToolbar(props: {
  onSearch: (searchText: string | undefined) => void;
}) {
  const { onSearch } = props;
  const [searchText, setSearchText] = useState('');
  return (
    <Stack direction="row-reverse">
      <IconButton
        onClick={() => onSearch(searchText === '' ? undefined : searchText)}
      >
        <Search />
      </IconButton>
      <TextField
        name="search"
        label="Search"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            onSearch(searchText === '' ? undefined : searchText);
          }
        }}
      />
    </Stack>
  );
}

function UsersPanel() {
  const [refetchRequired, setRefetchRequired] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);

  const updateDeletedStatus = useUpdateUserDeletedStatusMutation();
  const { data: customersData, refetch: refetchCustomers } =
    useListCustomersQuery(false);

  const handleDelete = useCallback(() => {
    if (selectedUser) {
      updateDeletedStatus[0]({ userId: selectedUser.id, isDeleted: true })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        })
        .finally(() => {
          setSelectedUser(null);
          setShowConfirmationModal(false);
        });
    }
  }, [updateDeletedStatus, selectedUser]);

  const handleRestore = useCallback(() => {
    if (selectedUser) {
      updateDeletedStatus[0]({ userId: selectedUser.id, isDeleted: false })
        .unwrap()
        .then(() => {
          setRefetchRequired(true);
        })
        .finally(() => {
          setSelectedUser(null);
          setShowConfirmationModal(false);
        });
    }
  }, [updateDeletedStatus, selectedUser]);

  const handleOpenEditUserModal = useCallback((user: User) => {
    setUserToEdit(user);
    setShowEditUserModal(true);
  }, []);

  const handleCloseEditUserModal = useCallback(() => {
    setShowEditUserModal(false);
    setUserToEdit(null);
  }, []);

  const handleOpenAddUserModal = useCallback(() => {
    setShowAddUserModal(true);
  }, []);

  const handleCloseAddUserModal = useCallback(() => {
    setShowAddUserModal(false);
  }, []);

  const handleUserUpdated = useCallback(() => {
    setRefetchRequired(true);
  }, []);

  const { data, isLoading, isFetching, refetch } = useListUsersQuery({});

  const columns: GridColDef<User>[] = [
    {
      field: 'id',
      headerName: 'User ID',
    },
    {
      field: 'name',
      headerName: 'User Name',
      flex: 0.5,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'role',
      headerName: 'User Role',
      flex: 0.5,
      renderCell: (params) => {
        const currentRole =
          typeof params.row.role === 'string'
            ? PortalRole[params.row.role as keyof typeof PortalRole]
            : params.row.role;

        return PortalRole[currentRole];
      },
    },
    {
      field: 'customerIds',
      headerName: 'Allowed Customers',
      sortable: false,
      flex: 1,
      renderCell: (cellValues) => {
        const user = cellValues.row as User;

        // Admin users have access to all customers
        const currentRole =
          typeof user.role === 'string'
            ? PortalRole[user.role as keyof typeof PortalRole]
            : user.role;

        if (currentRole === PortalRole.Admin) {
          return (
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: 'text.secondary',
              }}
            >
              All customers
            </Typography>
          );
        }

        // Get customer names from IDs
        const customerNames = (user.customerIds || [])
          .map((id) => customersData?.data.find((c) => c.id === id)?.name)
          .filter(Boolean);

        const customersText = customerNames.join(', ') || '-';

        return (
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              cursor: 'pointer',
            }}
            title={customerNames.join('\n')}
          >
            {customersText}
          </Typography>
        );
      },
    },
    {
      field: 'isDeleted',
      headerName: 'Is Deleted',
      flex: 0.5,
    },
    {
      field: 'lastModifiedAt',
      headerName: 'Last Modified [UTC]',
      flex: 1,
      valueFormatter: (v) => formatDateTime(v.value),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.5,
      renderCell: (params) => (
        <div>
          {!params.row.isDeleted && (
            <LoadingIconButton
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditUserModal(params.row);
              }}
            >
              <Tooltip title="Edit user">
                <EditOutlinedIcon />
              </Tooltip>
            </LoadingIconButton>
          )}
          {!params.row.isDeleted ? (
            <LoadingIconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(params.row);
                setShowConfirmationModal(true);
              }}
              loading={updateDeletedStatus[1].isLoading}
            >
              <Tooltip title="Delete user">
                <DeleteOutlineOutlinedIcon />
              </Tooltip>
            </LoadingIconButton>
          ) : (
            <LoadingIconButton
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(params.row);
                setShowConfirmationModal(true);
              }}
              loading={updateDeletedStatus[1].isLoading}
            >
              <Tooltip title="Restore user">
                <RestoreOutlinedIcon />
              </Tooltip>
            </LoadingIconButton>
          )}
        </div>
      ),
    },
  ];

  const [searchFilter, setSearchFilter] = useState<string>('');
  const onSearch = useCallback((searchText: string | undefined) => {
    setSearchFilter(searchText || '');
  }, []);

  const filteredUsers = useMemo(() => {
    if (!data?.data) return [];

    const users = data.data as User[];

    if (!searchFilter.trim()) {
      return users;
    }

    const searchTerm = searchFilter.toLowerCase().trim();
    return users.filter(
      (user) =>
        user.name.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm) ||
        user.id.toLowerCase().includes(searchTerm),
    );
  }, [data?.data, searchFilter]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (refetchRequired) {
      setRefetchRequired(false);
      refetchCustomers();
      refetch();
    }
  }, [refetchRequired, refetchCustomers, refetch]);

  return (
    <>
      <Dialog
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
      >
        <DialogTitle sx={{ padding: '2rem', fontSize: '2.5rem' }}>
          {selectedUser?.isDeleted ? 'Restore user' : 'Delete user'}
        </DialogTitle>
        <DialogContentText sx={{ padding: '2rem', fontSize: '1.5rem' }}>
          Are you sure you want to{' '}
          {selectedUser?.isDeleted ? 'restore' : 'delete'} user:{' '}
          <b>{selectedUser?.name}</b>?
          {!selectedUser?.isDeleted && (
            <>
              <br />
              This action can be undone later by restoring the user.
            </>
          )}
        </DialogContentText>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectedUser(null);
              setShowConfirmationModal(false);
            }}
            autoFocus
          >
            Cancel
          </Button>
          <LoadingButton
            loading={updateDeletedStatus[1].isLoading}
            variant="outlined"
            type="submit"
            onClick={selectedUser?.isDeleted ? handleRestore : handleDelete}
            sx={
              selectedUser?.isDeleted
                ? {
                    background: '#2e7d32',
                    color: 'white',
                    '&:hover': {
                      background: '#1b5e20',
                      boxShadow:
                        '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
                    },
                  }
                : {
                    background: '#f10101',
                    color: 'white',
                    '&:hover': {
                      background: '#ab0e0e',
                      boxShadow:
                        '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
                    },
                  }
            }
          >
            {selectedUser?.isDeleted ? 'Restore' : 'Confirm'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <CreateUserDialog
        open={showAddUserModal}
        onClose={handleCloseAddUserModal}
        onUserCreated={handleUserUpdated}
        customers={customersData?.data || []}
      />

      <EditUserDialog
        open={showEditUserModal}
        user={userToEdit}
        onClose={handleCloseEditUserModal}
        onUserUpdated={handleUserUpdated}
        customers={customersData?.data || []}
      />

      <PageCard
        title="Users"
        icon={<TableRowsOutlinedIcon />}
        sx={{
          '& .MuiCardHeader-root': {
            height: '80px',
          },
        }}
        contentSx={{ display: 'block' }}
        action={
          <>
            <RoleBased
              requiredRole={PortalRole[PortalRole.Admin]}
              component={
                <IconButton onClick={handleOpenAddUserModal}>
                  <Tooltip title="Add user">
                    <AddCircleOutlineOutlinedIcon />
                  </Tooltip>
                </IconButton>
              }
            />
            <LoadingIconButton
              loading={isLoading || isFetching}
              onClick={() => setRefetchRequired(true)}
            >
              <Tooltip title="Refresh">
                <CachedOutlinedIcon fontSize="large" />
              </Tooltip>
            </LoadingIconButton>
          </>
        }
      >
        <DataGrid
          columns={columns}
          columnVisibilityModel={{ id: false }}
          rows={filteredUsers}
          loading={isFetching}
          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            toolbar: {
              onSearch,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          sx={{
            '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
              display: 'none',
            },
            '& .MuiDataGrid-overlay': {
              height: '100%',
            },
            '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .deleted-user-row': {
              backgroundColor: '#f5f5f5',
              color: '#999',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
              '& .MuiIconButton-root': {
                color: 'inherit',
              },
              '& .MuiIconButton-root svg': {
                color: '#142A4D',
              },
            },
          }}
          getRowClassName={(params) =>
            params.row.isDeleted ? 'deleted-user-row' : ''
          }
          paginationMode="client"
          filterMode="client"
          pageSizeOptions={[10]}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          hideFooterSelectedRowCount
          disableRowSelectionOnClick
        />
      </PageCard>
    </>
  );
}

export default UsersPanel;
